import { useCallback, useEffect, useMemo, useState } from 'react';

import isNil from 'lodash/isNil';
import Pusher, { Channel } from 'pusher-js';

// Pusher configurations
const PUSHER_HOST = "soketi.brandtechbrain.com" || '';
const PUSHER_KEY = "kxh7hnv6jpjeAXD" || 'app-key';
const PUSHER_CLUSTER = "BDNAI_PRD" || 'APP_CLUSTER';
// Optional explicit port + TLS toggle for pointing at a local soketi (ws on 6001).
// Unset in deployed envs, so behaviour there is unchanged (TLS on the default port).
const PUSHER_PORT = "";
const PUSHER_FORCE_TLS = "" !== 'false';
const PUSHER_CHANNEL_PREFIX = 'client_channel';
const PUSHER_LOCAL_STORAGE_KEY = 'pusher-subscribes';

export interface SoketiMessage<T> {
  content: T;
  status_code: number;
  error?: any;
}

export interface SoketiResponse<T> {
  eventName: string;
  message: SoketiMessage<T>;
}

export const usePusher = (clientId: number) => {
  const [pusherChannel, setPusherChannel] = useState<Channel>();

  // Initialize Pusher
  const pusher = useMemo(() => {
    return new Pusher(PUSHER_KEY, {
      wsHost: PUSHER_HOST,
      cluster: PUSHER_CLUSTER,
      disableStats: true,
      enabledTransports: ['ws'],
      forceTLS: PUSHER_FORCE_TLS,
      ...(PUSHER_PORT
        ? { wsPort: Number(PUSHER_PORT), wssPort: Number(PUSHER_PORT) }
        : {}),
    });
  }, []);

  useEffect(
    function subscribeChannel() {
      if (!clientId || !pusher) {
        return;
      }

      const channelName = `${PUSHER_CHANNEL_PREFIX}_${clientId}`;
      if (pusherChannel?.name === channelName) {
        return;
      }
      const channel = pusher.subscribe(channelName);
      setPusherChannel(channel);

      localStorage.setItem(PUSHER_LOCAL_STORAGE_KEY, channelName);
      console.info(`Pusher - Subscribing "${channelName}" channel.`);
    },
    [clientId, pusher],
  );

  const unsubscribeChannel = useCallback(() => {
    const channelName = localStorage.getItem(PUSHER_LOCAL_STORAGE_KEY);
    if (channelName && pusher) {
      pusher.unsubscribe(channelName);
      localStorage.removeItem(PUSHER_LOCAL_STORAGE_KEY);
      console.info(`Pusher - Unsubscribing "${channelName}" channel.`);
    }
  }, [pusher]);

  useEffect(() => {
    function cleanup() {
      if (pusher) {
        unsubscribeChannel();
        pusher.disconnect();
      }
    }

    window.addEventListener('beforeunload', cleanup);

    return () => {
      window.removeEventListener('beforeunload', cleanup);
    };
  }, []);

  const bindEvent = useCallback(
    (eventName: string, callback: Function) => {
      if (isNil(pusherChannel)) return;

      pusherChannel.bind(eventName, callback);
      console.info(new Date().toTimeString(), '🚀 ~ Event binded:', eventName);
    },
    [pusherChannel],
  );

  const unbindEvent = useCallback(
    (eventName: string) => {
      if (isNil(pusherChannel)) return;

      pusherChannel.unbind(eventName);
      console.info(
        new Date().toTimeString(),
        '🚀 ~ Event unbinded:',
        eventName,
      );
    },
    [pusherChannel],
  );

  /** Invoke callback only after a disconnect → reconnect cycle (not the initial connect). */
  const bindOnReconnect = useCallback(
    (callback: () => void): (() => void) => {
      let sawDisconnect = false;

      const onDisconnected = () => {
        sawDisconnect = true;
      };

      const onConnected = () => {
        if (sawDisconnect) {
          sawDisconnect = false;
          callback();
        }
      };

      pusher.connection.bind('disconnected', onDisconnected);
      pusher.connection.bind('connected', onConnected);

      return () => {
        pusher.connection.unbind('disconnected', onDisconnected);
        pusher.connection.unbind('connected', onConnected);
      };
    },
    [pusher],
  );

  return {
    pusherChannel,
    bindEvent,
    unbindEvent,
    bindOnReconnect,
    unsubscribeChannel,
  };
};
